import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SettingsService from '../services/settingsService';

// ~0.1° ≈ 11km. Below this the user hasn't meaningfully moved, so we keep the
// previous coords object — React bails out of the state update and no consumer
// refetches. This is what keeps routine tab-switching from re-ranking the feed.
const MOVE_THRESHOLD_DEG = 0.1;

/**
 * Resolves the user's *current* location for location-aware feed ranking, but
 * only when the "Use my current location" setting is enabled. Returns
 * `{ lat, lng }` or `null` (caller falls back to the server-side home coords).
 *
 * Behavior:
 * - The `autoDetect` preference is fetched once on mount and refreshed only when
 *   settings change (via the `settingsUpdated` window event) — NOT on every tab
 *   focus, so we don't issue a settings round-trip each time the tab is shown.
 * - Geolocation is re-checked on mount and whenever the tab regains visibility,
 *   so reopening the app after landing in a new city re-ranks the feed without a
 *   hard reload. Uses a one-shot, low-accuracy, cached (`maximumAge`) fix — no
 *   `watchPosition` — so the battery/positioning cost is negligible and only
 *   ever incurred for users who opted in.
 */
export default function useCurrentLocation() {
  const { currentUser } = useAuth();
  const [coords, setCoords] = useState(null);
  const enabledRef = useRef(false);

  const detect = useCallback(() => {
    if (!enabledRef.current || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = { lat: position.coords.latitude, lng: position.coords.longitude };
        setCoords(prev => {
          if (prev &&
              Math.abs(prev.lat - next.lat) < MOVE_THRESHOLD_DEG &&
              Math.abs(prev.lng - next.lng) < MOVE_THRESHOLD_DEG) {
            return prev; // unchanged → no re-render, no refetch
          }
          return next;
        });
      },
      () => { /* denied/unavailable — caller falls back to stored home coords */ },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  // Load the autoDetect preference once, and refresh it when settings change.
  useEffect(() => {
    let cancelled = false;
    const loadFlag = async () => {
      try {
        const settings = await SettingsService.getUserSettings(currentUser?.id);
        if (cancelled) return;
        const enabled = !!settings?.location?.autoDetect;
        enabledRef.current = enabled;
        if (enabled) {
          detect();
        } else {
          setCoords(null); // turned off → revert to home-coords ranking
        }
      } catch {
        enabledRef.current = false;
      }
    };
    loadFlag();
    window.addEventListener('settingsUpdated', loadFlag);
    return () => {
      cancelled = true;
      window.removeEventListener('settingsUpdated', loadFlag);
    };
  }, [currentUser?.id, detect]);

  // Re-detect on tab refocus — reads the cached flag, no settings round-trip.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') detect();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [detect]);

  return coords;
}
